import { getLookupMeta, saveLookupEntries, saveLookupMeta } from "@seims/shared/src/services/db";
import { isAuthenticated } from "@seims/shared/src/security";
import { LOOKUP_CONFIG } from "@seims/shared/src/services/lookupService";

const LOOKUP_REFRESH_INTERVAL_MS = 1000 * 60 * 60 * 6; // 6 hours
export const LOOKUP_SYNC_EVENT = "eventsapp:lookup-sync";

const buildStatusRecord = (store, status, extra = {}) => ({
  store,
  status,
  updatedAt: new Date().toISOString(),
  ...extra,
});

const summarizeStatuses = (statuses) => ({
  queued: statuses.filter((entry) => entry.status === "queued"),
  syncing: statuses.filter((entry) => entry.status === "syncing"),
  skipped: statuses.filter((entry) => entry.status === "skipped"),
  synced: statuses.filter((entry) => entry.status === "synced"),
  failed: statuses.filter((entry) => entry.status === "failed"),
});

const derivePhase = (summary) => {
  if (summary.syncing.length > 0) {
    return "syncing";
  }

  if (summary.queued.length > 0) {
    return "queued";
  }

  if (summary.failed.length > 0) {
    return "completed";
  }

  if (summary.synced.length > 0 || summary.skipped.length > 0) {
    return "completed";
  }

  return "idle";
};

const notifyStatusChange = (snapshot, onStatusChange) => {
  onStatusChange?.(snapshot);

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(LOOKUP_SYNC_EVENT, { detail: snapshot }));
  }
};

const toEntries = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

export const LOOKUP_TARGETS = Object.entries(LOOKUP_CONFIG).map(
  ([store, config]) => ({
    store,
    label: config.label,
    fetcher: config.fetcher,
  })
);

const LOOKUP_LABELS = Object.fromEntries(
  LOOKUP_TARGETS.map((target) => [target.store, target.label])
);

const shouldRefreshLookup = async (store, force = false) => {
  if (force) {
    return true;
  }

  const meta = await getLookupMeta(store);
  if (!meta?.lastModified) {
    return true;
  }

  const last = Number(meta.lastModified);
  if (Number.isNaN(last)) {
    return true;
  }

  return Date.now() - last >= LOOKUP_REFRESH_INTERVAL_MS;
};

export const syncLookupStore = async (store, fetcher, force = false) => {
  if (typeof force === "object" && force !== null) {
    return syncLookupStore(store, fetcher, force.force ?? false);
  }

  const refresh = await shouldRefreshLookup(store, force);
  if (!refresh) {
    return buildStatusRecord(store, "skipped", {
      reason: "fresh",
      label: LOOKUP_LABELS[store] || store,
    });
  }

  const response = await fetcher();
  const entries = toEntries(response);

  await saveLookupEntries(store, entries);
  await saveLookupMeta(store, Date.now().toString());

  return buildStatusRecord(store, "synced", {
    count: entries.length,
    label: LOOKUP_LABELS[store] || store,
  });
};

export const preloadOfflineLookups = async ({ force = false, onStatusChange } = {}) => {

  // Only prefload for authenticated session
  if (!isAuthenticated()) { return; }

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    const offlineSnapshot = {
      phase: "skipped",
      reason: "offline",
      statuses: [],
      queued: [],
      syncing: [],
      skippedStores: [],
      synced: [],
      failed: [],
    };

    notifyStatusChange(offlineSnapshot, onStatusChange);
    return offlineSnapshot;
  }

  const statusMap = new Map();

  const emitSnapshot = () => {
    const statuses = Array.from(statusMap.values());
    const summary = summarizeStatuses(statuses);
    const snapshot = {
      phase: derivePhase(summary),
      statuses,
      ...summary,
      skippedStores: summary.skipped,
    };

    notifyStatusChange(snapshot, onStatusChange);
    return snapshot;
  };

  LOOKUP_TARGETS.forEach((target) => {
    statusMap.set(
      target.store,
      buildStatusRecord(target.store, "queued", { label: target.label })
    );
  });

  emitSnapshot();

  const settled = await Promise.allSettled(
    LOOKUP_TARGETS.map(async (target) => {
      statusMap.set(
        target.store,
        buildStatusRecord(target.store, "syncing", { label: target.label })
      );
      emitSnapshot();

      try {
        const result = await syncLookupStore(target.store, target.fetcher, force);
        statusMap.set(target.store, result);
        emitSnapshot();
        return result;
      } catch (error) {
        const failed = buildStatusRecord(target.store, "failed", {
          label: target.label,
          reason: error?.message || "lookup sync failed",
        });
        statusMap.set(target.store, failed);
        emitSnapshot();
        throw error;
      }
    })
  );

  settled.forEach((result, index) => {
    if (result.status === "fulfilled") {
      return;
    }

    const store = LOOKUP_TARGETS[index].store;
    statusMap.set(
      store,
      buildStatusRecord(store, "failed", {
        label: LOOKUP_LABELS[store] || store,
        reason: result.reason?.message || "lookup sync failed",
      })
    );
  });

  return emitSnapshot();
};
